"use client";

import React, { useEffect, useState } from "react";
import { Menu } from "lucide-react";
import Image from "next/image";
import { useTranslations } from 'next-intl';
import { Link as I18nLink } from "@/i18n/routing";

import { Button } from "@/components/ui/button";
import {
  NavigationMenu,
  NavigationMenuList,
} from "@/components/ui/navigation-menu";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";

type MenuItem = {
  title: string;
  url: string;
  description?: string;
  icon?: React.ReactNode;
  items?: MenuItem[];
};

const Navbar = () => {
  const t = useTranslations('navbar');
  const [open, setOpen] = useState(false);

  const logo = {
    url: "#",
    src: "/images/logo.png",
    alt: t('logo.alt'),
    title: t('logo.title'),
  };
  
  const menu = [
    { title: t('menu.howItWorks'), url: "#how-it-works" },
    { title: t('menu.examples'), url: "#examples" },
    { title: t('menu.pricing'), url: "#pricing" },
  ];
  
  const getStarted = {
    text: t('getStarted'),
    url: "/try",
  };

  useEffect(() => {
    document.documentElement.style.scrollPaddingTop = '100px';
    
    return () => {
      document.documentElement.style.scrollPaddingTop = '';
    };
  }, []);
  
  const scrollToTop = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  
  const scrollToSection = (e: React.MouseEvent<HTMLAnchorElement>, url: string) => {
    e.preventDefault();
    
    if (!url.startsWith('#')) return;
    
    const sectionId = url.replace('#', '');
    const section = document.getElementById(sectionId);
    
    if (section) {
      const navbar = document.querySelector('nav');
      const navbarHeight = navbar ? navbar.offsetHeight : 0;
      
      const sectionTop = section.getBoundingClientRect().top + window.scrollY;
      const offsetPosition = sectionTop - navbarHeight - 20;
      
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      });
    }
  };

  const handleLogoClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    scrollToTop(e);
  };
  
  const handleMobileLogoClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    scrollToTop(e);
    setOpen(false);
  };

  const handleMenuItemClick = (e: React.MouseEvent<HTMLAnchorElement>, url: string) => {
    scrollToSection(e, url);
  };
  
  const handleMobileMenuItemClick = (e: React.MouseEvent<HTMLAnchorElement>, url: string) => {
    scrollToSection(e, url);
    setOpen(false);
  };

  const renderLogo = (
    onClick: (e: React.MouseEvent<HTMLAnchorElement>) => void,
    className?: string
  ) => (
    <a href="#" className="flex items-center gap-2" onClick={onClick}>
      <Image 
        src={logo.src} 
        width={40} 
        height={40} 
        className={`w-10 ${className || ''}`} 
        alt={logo.alt} 
      />
      <span className="text-white font-medium text-xl ms-2">{logo.title}</span>
    </a>
  );

  const renderMenuItem = (item: MenuItem) => (
    <a
      key={item.title}
      className="text-lg font-medium text-white/90 hover:text-white transition-colors cursor-pointer"
      href={item.url}
      onClick={(e) => handleMenuItemClick(e, item.url)}
    >
      {item.title}
    </a>
  );

  const renderMobileMenuItem = (item: MenuItem) => (
    <a 
      key={item.title} 
      href={item.url} 
      className="text-xl font-medium py-2 cursor-pointer"
      onClick={(e) => handleMobileMenuItemClick(e, item.url)}
    >
      {item.title}
    </a>
  );

  return (
    <section className="fixed top-0 left-0 right-0 z-50">
      <div className="container max-w-7xl mx-auto px-4 py-6">
        {/* Desktop navbar */}
        <nav className="hidden justify-between lg:flex bg-background/50 backdrop-blur-lg rounded-full px-6 py-4">
          <div className="flex-1">
            {renderLogo(handleLogoClick)}
          </div>
          <div className="flex-1 flex justify-center">
            <NavigationMenu>
              <NavigationMenuList className="gap-8">
                {menu.map(renderMenuItem)}
              </NavigationMenuList>
            </NavigationMenu>
          </div>
          <div className="flex-1 flex justify-end">
            <Button asChild variant="outline" size="lg" className="font-bold rounded-full px-8 bg-white text-background">
              <I18nLink href={getStarted.url}>{getStarted.text}</I18nLink>
            </Button>
          </div>
        </nav>
        
        {/* Mobile navbar */}
        <div className="block lg:hidden">
          <div className="flex items-center justify-between bg-background/50 backdrop-blur-md rounded-full px-4 py-3">
            {renderLogo(handleLogoClick)}
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <Button variant="outline" size="icon" className="rounded-full">
                  <Menu className="size-5" />
                </Button>
              </SheetTrigger>
              <SheetContent className="px-6">
                <SheetHeader className="pt-4 ps-0">
                  <SheetTitle>
                    {renderLogo(handleMobileLogoClick)}
                  </SheetTitle>
                </SheetHeader>
                <div className="my-12 flex flex-col gap-8">
                  <div className="flex flex-col gap-6">
                    {menu.map(renderMobileMenuItem)}
                  </div>
                  <div className="flex flex-col gap-3">
                    <Button asChild size="lg" className="font-bold rounded-full">
                      <I18nLink href={getStarted.url}>{getStarted.text}</I18nLink>
                    </Button>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </section>
  );
};

export { Navbar };
